<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { api, ApiError, yuan } from '../api.js'
import { displayNumber } from '../display-number.js'
import type { Item } from '../types.js'
import SaleLibrary from './SaleLibrary.vue'

defineProps<{ account: string }>()
const items = ref<Item[]>([])
const library = ref('purchase')
const error = ref('')
const editing = ref<Item | null>(null)
const historyItem = ref<Item | null>(null)
const history = ref<any[]>([])
const customChoice = '__custom__'
const form = reactive({ name: '', expenseKind: 'daily' as 'daily' | 'incidental', defaultPrice: '', conversionFactor: '', fixedDaily: true })
const categoryChoice = ref('食材')
const customCategory = ref('')
const unitChoice = ref('')
const customUnit = ref('')
const baseUnitChoice = ref('个')
const customBaseUnit = ref('')
const useConversion = ref(false)
const standardUnits = ['斤','公斤','克','个','颗','盘','捆','袋','盒','瓶','桶','箱','包','张','套','杯','碗','份','次','度']
const standardCategories = computed(() => form.expenseKind === 'daily'
    ? ['食材','调料','包装','其他']
    : ['厨具设备','维修','水电燃气','包装','其他'])
const categories = computed(() => [...new Set([
  ...standardCategories.value,
  ...items.value.filter((item) => item.kind === 'purchase' && item.expenseKind === form.expenseKind).map((item) => item.category).filter(Boolean),
])])
const units = computed(() => [...new Set([...standardUnits, ...items.value.filter((item) => item.kind === 'purchase').map((item) => item.unit).filter(Boolean)])])

function resolved(choice: string, custom: string) { return choice === customChoice ? custom.trim() : choice }
function defaultCategory() { return form.expenseKind === 'daily' ? '食材' : '厨具设备' }
function resetForm() {
  Object.assign(form, { name: '', expenseKind: 'daily', defaultPrice: '', conversionFactor: '', fixedDaily: true })
  categoryChoice.value = '食材'
  customCategory.value = ''
  unitChoice.value = ''
  customUnit.value = ''
  baseUnitChoice.value = '个'
  customBaseUnit.value = ''
  useConversion.value = false
}
function loadChoice(value: string, options: string[], choice: { value: string }, custom: { value: string }) {
  if (!value || options.includes(value)) { choice.value = value; custom.value = '' }
  else { choice.value = customChoice; custom.value = value }
}

async function load() {
  try { items.value = (await api<{ items: Item[] }>('/api/items')).items } catch (reason) { error.value = (reason as Error).message }
}
async function saveItem() {
  error.value = ''
  try {
    const payload = {
      kind: 'purchase',
      expenseKind: form.expenseKind,
      name: form.name,
      category: resolved(categoryChoice.value, customCategory.value),
      unit: resolved(unitChoice.value, customUnit.value),
      baseUnit: useConversion.value ? resolved(baseUnitChoice.value, customBaseUnit.value) : '',
      conversionFactor: useConversion.value ? form.conversionFactor : '',
      defaultPrice: form.defaultPrice,
      fixedDaily: form.expenseKind === 'daily' ? form.fixedDaily : false,
    }
    if (editing.value) await api(`/api/items/${editing.value.id}`, { method: 'PATCH', operationId: crypto.randomUUID(), body: JSON.stringify({ ...payload, expectedVersion: editing.value.version, enabled: editing.value.enabled }) })
    else await api('/api/items', { method: 'POST', operationId: crypto.randomUUID(), body: JSON.stringify(payload) })
    editing.value = null
    resetForm()
    await load()
  } catch (reason) { error.value = (reason as ApiError).message }
}
function edit(item: Item) {
  editing.value = item
  Object.assign(form, { name: item.name, expenseKind: item.expenseKind ?? 'daily', conversionFactor: item.conversionFactor ? Number(item.conversionFactor).toString() : '', defaultPrice: yuan(item.defaultPriceFen), fixedDaily: item.fixedDaily })
  loadChoice(item.category, standardCategories.value, categoryChoice, customCategory)
  loadChoice(item.unit, standardUnits, unitChoice, customUnit)
  useConversion.value = Boolean(item.baseUnit && item.conversionFactor)
  loadChoice(item.baseUnit ?? '个', standardUnits, baseUnitChoice, customBaseUnit)
  window.scrollTo({ top: 100, behavior: 'smooth' })
}
function cancelEdit() { editing.value = null; resetForm() }
async function showHistory(item: Item) { historyItem.value = item; history.value = (await api<{ records: any[] }>(`/api/items/${item.id}/history?page=1`)).records }
async function toggle(item: Item) {
  try {
    const result = await api<{ item: Item }>(`/api/items/${item.id}`, { method: 'PATCH', operationId: crypto.randomUUID(), body: JSON.stringify({ expectedVersion: item.version, name: item.name, category: item.category, unit: item.unit, baseUnit: item.baseUnit ?? '', conversionFactor: item.conversionFactor ?? '', defaultPrice: yuan(item.defaultPriceFen), fixedDaily: item.fixedDaily, enabled: !item.enabled }) })
    Object.assign(item, result.item)
  } catch (reason) { error.value = (reason as ApiError).message }
}
watch(() => form.expenseKind, () => {
  if (!editing.value) categoryChoice.value = defaultCategory()
})
onMounted(load)
</script>

<template>
  <main class="page my-page">
    <header class="account-line"><span>共用账号</span><strong>{{ account }}</strong></header>
    <div class="segmented two"><button :class="{active:library==='purchase'}" @click="library='purchase'">采购项目</button><button :class="{active:library==='sale'}" @click="library='sale'">售卖商品</button></div>
    <SaleLibrary v-if="library==='sale'" />
    <section v-else class="library-section"><div class="section-title"><div><p class="eyebrow">今日页的食材、单位和换算都来自这里</p><h1>采购项目管理</h1></div></div>
      <form class="item-form" @submit.prevent="saveItem">
        <label>名称<input v-model="form.name" placeholder="例如 鸡蛋" required /></label>
        <label>类型<select v-model="form.expenseKind" :disabled="Boolean(editing)"><option value="daily">每日采购</option><option value="incidental">日常支出</option></select></label>
        <label>分类<select v-model="categoryChoice"><option v-for="category in categories" :key="category" :value="category">{{ category }}</option><option :value="customChoice">＋ 自定义分类</option></select></label>
        <label v-if="categoryChoice === customChoice" class="custom-choice">自定义分类<input v-model="customCategory" placeholder="输入分类名称" required /></label>
        <label>单位<select v-model="unitChoice"><option value="">不使用单位</option><option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option><option :value="customChoice">＋ 自定义单位</option></select></label>
        <label v-if="unitChoice === customChoice" class="custom-choice">自定义单位<input v-model="customUnit" placeholder="输入单位名称" required /></label>
        <label>默认价格（元）<input v-replace-number="form.defaultPrice" v-model="form.defaultPrice" inputmode="decimal" placeholder="可不填" /></label>
        <template v-if="form.expenseKind==='daily'">
          <label class="check-label"><input v-model="form.fixedDaily" type="checkbox" /> 每日自动带出</label>
          <label class="check-label"><input v-model="useConversion" type="checkbox" /> 需要单位折算</label>
          <template v-if="useConversion">
            <label>基础单位<select v-model="baseUnitChoice"><option v-for="unit in units" :key="unit" :value="unit">{{ unit }}</option><option :value="customChoice">＋ 自定义单位</option></select></label>
            <label v-if="baseUnitChoice === customChoice" class="custom-choice">自定义基础单位<input v-model="customBaseUnit" placeholder="输入单位名称" required /></label>
            <label>每单位折算<input v-replace-number="form.conversionFactor" v-model="form.conversionFactor" inputmode="decimal" placeholder="例如 30" required /></label>
          </template>
        </template>
        <button class="primary-button">{{ editing ? '保存修改' : '新增项目' }}</button><button v-if="editing" type="button" class="text-button" @click="cancelEdit">取消编辑</button>
      </form>
      <p v-if="error" class="form-error">{{ error }}</p>
      <div class="item-list"><article v-for="item in items.filter((entry) => entry.kind === 'purchase')" :key="item.id" :class="{disabled:!item.enabled}"><div><b>{{ item.name }}</b><small>{{ item.category || '未分类' }} · {{ item.unit || '无单位' }}<template v-if="item.baseUnit && item.conversionFactor"> · 1 {{ item.unit }} = {{ displayNumber(item.conversionFactor) }} {{ item.baseUnit }}</template></small></div><div class="item-actions"><button @click="showHistory(item)">历史</button><button @click="edit(item)">编辑</button><button @click="toggle(item)">{{ item.enabled ? '停用' : '启用' }}</button></div></article></div>
      <section v-if="historyItem" class="inline-history"><div class="section-title"><h2>{{ historyItem.name }} · 历史</h2><button class="text-button" @click="historyItem=null">关闭</button></div><div v-for="row in history" :key="row.id" class="compact-row"><div><b>{{ row.businessDate }}</b><small>{{ displayNumber(row.quantity) }} {{ row.unitSnapshot }} · 单价 ¥{{ yuan(row.unitPriceFen) }}</small></div><strong>¥{{ yuan(row.amountFen) }}</strong></div><p v-if="!history.length" class="empty-copy">暂无历史记录。</p></section>
    </section>
  </main>
</template>
